import {
  getVoiceConfigByCharacter,
  getVoicePathByFilename,
} from "./voiceAssetRegistry";

// 재생 시간 계산 설정
// CHARACTERS_PER_GROUP: 한 그룹당 글자 수 (10자)
// SECONDS_PER_GROUP: 한 그룹당 재생 시간(초) (0.5초)
const PLAYBACK_CONFIG = {
  CHARACTERS_PER_GROUP: 10,
  SECONDS_PER_GROUP: 0.5,
} as const;

/**
 * 텍스트 길이를 기반으로 목표 재생 시간을 계산합니다.
 * 계산 방식: Math.floor(textLength / 10) * 0.5 + (나머지 > 0 ? 0.5 : 0)
 */
const calculatePlaybackDuration = (textLength: number): number => {
  const groups = Math.floor(textLength / PLAYBACK_CONFIG.CHARACTERS_PER_GROUP);
  const remainder = textLength % PLAYBACK_CONFIG.CHARACTERS_PER_GROUP;
  const baseDuration = groups * PLAYBACK_CONFIG.SECONDS_PER_GROUP;

  if (remainder > 0) {
    return baseDuration + PLAYBACK_CONFIG.SECONDS_PER_GROUP;
  }

  return baseDuration;
};

const generateVoiceSequence = (
  targetDuration: number,
  artist: string,
  character: string,
  intent: string,
): string[] => {
  const voiceConfig = getVoiceConfigByCharacter(artist, character, intent);

  if (!voiceConfig) {
    return [];
  }

  const availableDurations = Object.keys(voiceConfig)
    .map(Number)
    .sort((a, b) => a - b);

  if (availableDurations.length === 0) {
    return [];
  }

  const sequence: string[] = [];
  let currentDuration = 0;

  // 1-3단계: 목표 재생 시간에 맞춰 오디오 파일 시퀀스 생성
  while (currentDuration < targetDuration) {
    const remainingTime = targetDuration - currentDuration;
    // 1단계: 목표 재생 시간 내에서 사용 가능한 오디오 파일 길이를 찾습니다
    const possibleDurations = availableDurations.filter(
      (duration) => duration <= remainingTime,
    );

    if (possibleDurations.length === 0) {
      break;
    }

    // 2단계: 남은 시간보다 작거나 같은 길이의 파일 중 랜덤으로 선택합니다
    const randomIndex = Math.floor(Math.random() * possibleDurations.length);
    const selectedDuration = possibleDurations[randomIndex];
    const files = voiceConfig[selectedDuration.toString()];

    if (files && files.length > 0) {
      const randomFileIndex = Math.floor(Math.random() * files.length);
      const selectedFile = files[randomFileIndex];
      // 3단계: 선택된 파일을 시퀀스에 추가하고 남은 시간을 업데이트합니다
      sequence.push(selectedFile);
      currentDuration += selectedDuration;
    } else {
      break;
    }
  }

  // 4단계: 시퀀스가 비어있을 때 최소 1개 파일 보장
  if (sequence.length === 0) {
    const shortestDuration = availableDurations[0];
    const files = voiceConfig[shortestDuration.toString()];
    if (files && files.length > 0) {
      const randomFileIndex = Math.floor(Math.random() * files.length);
      sequence.push(files[randomFileIndex]);
    }
  }

  return sequence;
};

class VoicePlayerManager {
  private currentVoice: HTMLAudioElement | null = null;
  private audioInstance: HTMLAudioElement | null = null;
  private isPlaying = false;
  private isAudioActivated = false;
  private activatingPromise: Promise<void> | null = null;

  private initializeAudioInstance = (): HTMLAudioElement => {
    if (!this.audioInstance) {
      this.audioInstance = new Audio();
    }
    return this.audioInstance;
  };

  /**
   * 오디오 인스턴스를 재사용하기 위해 상태를 초기화합니다.
   * 재생을 중지하고 이벤트 핸들러를 제거하지만 인스턴스는 유지합니다.
   */
  private resetAudioInstance = (): void => {
    if (this.audioInstance) {
      this.audioInstance.pause();
      this.audioInstance.currentTime = 0;
      this.audioInstance.onended = null;
      this.audioInstance.onerror = null;
      this.audioInstance.onloadeddata = null;
    }
  };

  /**
   * 오디오 인스턴스를 완전히 정리하고 메모리에서 해제합니다.
   * 재생을 중지하고 모든 이벤트 핸들러를 제거한 후 인스턴스를 null로 설정합니다.
   */
  private cleanupAudioInstance = (): void => {
    if (this.audioInstance) {
      this.audioInstance.pause();
      this.audioInstance.currentTime = 0;
      this.audioInstance.src = "";
      this.audioInstance.onended = null;
      this.audioInstance.onerror = null;
      this.audioInstance.onloadeddata = null;
      this.audioInstance = null;
    }
  };

  stopCurrentPlayback = (): void => {
    if (this.audioInstance) {
      this.audioInstance.pause();
      this.audioInstance.currentTime = 0;
    }
    if (this.currentVoice) {
      this.currentVoice = null;
    }
  };

  stopAll = (clearInstance: boolean = false): void => {
    this.stopCurrentPlayback();
    this.isPlaying = false;
    if (clearInstance) {
      this.cleanupAudioInstance();
    }
  };

  /**
   * 브라우저 자동재생 정책을 우회하기 위해 오디오 컨텍스트를 활성화합니다.
   * 더미 오디오 파일을 재생하여 사용자 상호작용을 시뮬레이션합니다.
   * 이미 활성화되어 있으면 아무 작업도 수행하지 않습니다.
   */
  private doActivateAudio = async (): Promise<void> => {
    const audio = this.initializeAudioInstance();
    this.resetAudioInstance();
    audio.volume = 0;
    audio.src =
      "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSBzyfDekD8KE1uy6O2jUxE=";
    try {
      await audio.play();
      await new Promise((resolve) => setTimeout(resolve, 100));
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;
      this.isAudioActivated = true;
    } catch (error) {
      console.error("Failed to activate audio:", error);
      throw error;
    } finally {
      this.activatingPromise = null;
    }
  };

  activateAudio = async (): Promise<void> => {
    if (this.isAudioActivated) {
      return;
    }
    if (!this.activatingPromise) {
      this.activatingPromise = this.doActivateAudio();
    }
    return this.activatingPromise;
  };

  deactivateAudio = (): void => {
    if (this.isPlaying) {
      this.stopAll();
    }
    this.isAudioActivated = false;
    this.activatingPromise = null;
  };

  getAudioActivationState = (): boolean => {
    return this.isAudioActivated;
  };

  /**
   * 음성 파일 시퀀스를 순차적으로 재생합니다.
   * 각 파일은 이전 파일이 완료된 후 재생되며, 10초 타임아웃이 설정됩니다.
   * 오디오가 활성화되지 않으면 경고를 출력하고 재생하지 않습니다.
   */
  playVoiceSequence = async (
    voicePaths: string[],
    artist: string,
    character: string,
  ): Promise<void> => {
    if (!this.isAudioActivated) {
      console.warn(
        "Audio is not activated. Please activate audio before playing voice.",
      );
      return;
    }

    this.stopAll();

    this.isPlaying = true;

    const audio = this.initializeAudioInstance();

    for (const filename of voicePaths) {
      if (!this.isPlaying) {
        break;
      }

      const voicePath = getVoicePathByFilename(artist, character, filename);

      if (!voicePath) {
        continue;
      }

      this.resetAudioInstance();
      this.currentVoice = audio;

      try {
        await new Promise<void>((resolve, reject) => {
          if (!this.audioInstance) {
            reject(new Error("Audio instance is not available"));
            return;
          }

          if (!voicePath || voicePath.trim() === "") {
            reject(new Error(`Invalid voice path for file: ${filename}`));
            return;
          }

          let isResolved = false;
          let timeoutId: NodeJS.Timeout | null = null;

          // 이벤트 핸들러와 타임아웃을 정리하는 함수
          const cleanup = () => {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            if (this.audioInstance) {
              this.audioInstance.onloadeddata = null;
              this.audioInstance.onended = null;
              this.audioInstance.onerror = null;
            }
          };

          // 오디오 파일 로드 완료 시 재생 시작
          const handleLoadedData = () => {
            if (isResolved) {
              return;
            }
            if (!this.audioInstance) {
              cleanup();
              reject(new Error("Audio instance is not available"));
              return;
            }
            this.audioInstance.play().catch((error) => {
              if (!isResolved) {
                isResolved = true;
                cleanup();
                reject(
                  new Error(
                    `Failed to play voice ${filename}: ${error.message}`,
                  ),
                );
              }
            });
          };

          // 오디오 재생 완료 시 다음 파일로 진행
          const handleEnded = () => {
            if (!isResolved) {
              isResolved = true;
              cleanup();
              resolve();
            }
          };

          // 오디오 로드/재생 에러 처리
          const handleError = () => {
            if (!isResolved) {
              isResolved = true;
              cleanup();

              let errorMessage = `Failed to load or play voice: ${filename}`;

              if (this.audioInstance?.error) {
                const error = this.audioInstance.error;
                let errorDetail = "";

                if (error.code === MediaError.MEDIA_ERR_ABORTED) {
                  errorDetail = "Aborted";
                } else if (error.code === MediaError.MEDIA_ERR_NETWORK) {
                  errorDetail = "Network error";
                } else if (error.code === MediaError.MEDIA_ERR_DECODE) {
                  errorDetail = "Decode error";
                } else if (
                  error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
                ) {
                  errorDetail = "Source not supported";
                }

                errorMessage = `${errorMessage} (${errorDetail})`;
              }

              reject(new Error(errorMessage));
            }
          };

          // 10초 타임아웃 처리 (네트워크 문제나 파일 로드 실패 감지)
          const handleTimeout = () => {
            if (!isResolved) {
              isResolved = true;
              cleanup();
              reject(
                new Error(
                  `Timeout loading voice file: ${filename}. Network may be slow or file may be unavailable.`,
                ),
              );
            }
          };

          this.audioInstance.onloadeddata = handleLoadedData;
          this.audioInstance.onended = handleEnded;
          this.audioInstance.onerror = handleError;

          try {
            this.audioInstance.src = voicePath;
            this.audioInstance.load();

            // 10초 타임아웃 설정
            timeoutId = setTimeout(handleTimeout, 10000);
          } catch (error) {
            cleanup();
            reject(
              new Error(
                `Failed to set audio source for ${filename}: ${error instanceof Error ? error.message : String(error)}`,
              ),
            );
          }
        });
      } catch (error) {
        console.error(`Error playing voice ${filename}:`, error);
        this.resetAudioInstance();
      } finally {
        if (this.currentVoice === audio) {
          this.currentVoice = null;
        }
      }
    }

    this.isPlaying = false;
  };
}

export const voicePlayerManager = new VoicePlayerManager();

export {calculatePlaybackDuration, generateVoiceSequence};
